/**
 * PerformancePreferenceStep -- Select first and second performance preferences.
 *
 * The 1st preference is a named performance from the rodeo (e.g.
 * "Performance 1"). The 2nd preference is gated by the rodeo's admin-defined
 * `preference_config` (slack / out / performance categories) plus a hard rule
 * that the 2nd value must differ from the 1st.
 *
 * Edge case: when the admin has narrowed the allowed list down to nothing
 * for the chosen 1st preference, the form renders a read-only "Must choose
 * Out" label and forces second_performance_preference="Draw Out" on submit.
 */
import { useEffect, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Lock, AlertTriangle } from 'lucide-react';
import { formatPreferenceLabel } from '@/utils/format';

type PreferenceOption = 'slack' | 'out' | 'performance';

interface PreferenceConfig {
  ifPerformance: PreferenceOption[];
  ifSlack: PreferenceOption[];
}

/**
 * Rich performance choice with a unique stored value plus a human-readable
 * label. Lets the buddy-group picker disambiguate same-named performances
 * by date/time without forcing the per-event registration form (which only
 * has names) to change shape.
 */
export interface PerformanceChoice {
  /** Stored preference value (also used as the React key). */
  value: string;
  /** Human-readable label, e.g. "Performance 1 — Fri May 15, 6:00 PM". */
  label: string;
  /** True when this row is a slack performance (`is_slack=true`). */
  isSlack: boolean;
}

interface PerformancePreferenceStepProps {
  /**
   * Legacy: list of named performance names. Slack is always appended as
   * a single "Slack" sentinel option. Used by the per-event registration
   * modal where date/time is not available.
   */
  performances?: string[];
  /**
   * Preferred: explicit option list including any slack rows. When
   * supplied this overrides `performances` and lets the caller render
   * each performance with its own date/time label and unique value.
   */
  performanceOptions?: PerformanceChoice[];
  firstPreference: string;
  secondPreference: string;
  onFirstChange: (value: string) => void;
  onSecondChange: (value: string) => void;
  /** Admin-controlled gating from the rodeo. Optional for back-compat. */
  preferenceConfig?: PreferenceConfig | null;
  error?: string;
  /**
   * When true, both preference fields are rendered disabled and a hint
   * tells the user the values were set by the buddy group primary
   * cardholder. The user cannot edit them per-event — only the primary
   * cardholder can change them via the buddy group setup page.
   */
  lockedByBuddyGroup?: boolean;
  /**
   * Compact layout — used when this component is embedded inside a larger
   * step (e.g. the Position & Partner step of the registration wizard)
   * rather than presented as its own dedicated step. The two selects
   * render side-by-side on md+ screens and the verbose hint text is
   * suppressed so the section fits without crowding the surrounding
   * fields.
   */
  compact?: boolean;
  /**
   * The user's last preference selection from another event in the SAME
   * rodeo, if any. Used by the registration wizard to (a) default the
   * picks for the new entry to those values up front and (b) warn when
   * the user changes the selection mid-wizard so their entries no longer
   * line up. Pass `null` (or omit) when there's no prior entry to compare
   * against.
   */
  previousPreferences?: { first: string; second: string | null } | null;
  /**
   * IANA timezone of the parent rodeo. Threaded into every preference
   * label render in this component so the "doesn't match your last
   * entry" warning uses the host's saved zone with abbreviation
   * appended ("Fri, May 15, 6:00 PM PT"), not the viewer's local zone.
   */
  timezone?: string | null;
}

const SLACK_VALUE = 'Slack';
const DRAW_OUT_VALUE = 'Draw Out';

/**
 * Categorise a stored preference string for gating purposes. Strips the
 * optional trailing ` [<isoDatetime>]` suffix the buddy-group picker
 * appends to disambiguate same-named performances so both old ("Slack")
 * and new ("Slack [2026-05-15T11:00:00Z]") values reduce to the same
 * category. Mirrored on the backend in
 * `services/registration/preference-config.ts`.
 */
function categorize(value: string | null | undefined): PreferenceOption | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const head = trimmed.replace(/\s*\[[^\]]*\]\s*$/, '');
  const lower = head.toLowerCase();
  if (lower === 'slack') return 'slack';
  if (lower === 'out' || lower === 'draw out' || lower === 'drawout') return 'out';
  return 'performance';
}

const DEFAULT_CONFIG: PreferenceConfig = {
  ifPerformance: ['slack', 'out', 'performance'],
  ifSlack: ['slack', 'out', 'performance'],
};

export function PerformancePreferenceStep({
  performances,
  performanceOptions,
  firstPreference,
  secondPreference,
  onFirstChange,
  onSecondChange,
  preferenceConfig,
  error,
  lockedByBuddyGroup = false,
  compact = false,
  previousPreferences = null,
  timezone = null,
}: PerformancePreferenceStepProps) {
  const config = preferenceConfig ?? DEFAULT_CONFIG;

  // Resolve the canonical option list. Callers that have rich data
  // (BuddyGroupSetupPage) supply `performanceOptions` directly; the
  // legacy per-event registration modal still passes a string array of
  // names, which we map into trivial options with no date/time and
  // append the single "Slack" sentinel.
  //
  // We also defensively splice in any current selection that doesn't
  // match an option — typically a locked buddy-group preference
  // ("Performance 2 [<iso>]") flowing through a caller that only knows
  // names. Without the splice, Radix Select renders a blank trigger and
  // the user has no idea what slot they're locked into.
  const allOptions = useMemo<PerformanceChoice[]>(() => {
    const base: PerformanceChoice[] =
      performanceOptions && performanceOptions.length > 0
        ? [...performanceOptions]
        : [
            ...(performances ?? []).map((name) => ({
              value: name,
              label: name,
              isSlack: false,
            })),
            { value: SLACK_VALUE, label: SLACK_VALUE, isSlack: true },
          ];
    const ensureGhost = (val: string) => {
      if (!val) return;
      if (val === DRAW_OUT_VALUE) return;
      if (base.some((o) => o.value === val)) return;
      const cat = categorize(val);
      base.push({
        value: val,
        label: formatPreferenceLabel(val, timezone) || val,
        isSlack: cat === 'slack',
      });
    };
    ensureGhost(firstPreference);
    ensureGhost(secondPreference);
    return base;
  }, [performanceOptions, performances, firstPreference, secondPreference, timezone]);

  // Categorise the current 1st preference: prefer the option's `isSlack`
  // flag (authoritative even when the value carries a [datetime] suffix
  // we'd otherwise have to parse) and fall back to the string heuristic
  // for "Draw Out" / unknown values.
  const firstCategory = useMemo<PreferenceOption | null>(() => {
    if (!firstPreference) return null;
    const opt = allOptions.find((o) => o.value === firstPreference);
    if (opt) return opt.isSlack ? 'slack' : 'performance';
    return categorize(firstPreference);
  }, [firstPreference, allOptions]);

  // 1st preference list: every available performance row (named + slack).
  // No name-based dedupe — the consumer pre-builds unique values so each
  // row is selectable on its own (e.g. two "Performance 1"s on different
  // days each render as a distinct option).
  const firstOptions = allOptions;

  // Allowed 2nd-preference categories given the 1st category. We honor the
  // admin's `ifPerformance` / `ifSlack` checkboxes verbatim — when the admin
  // has enabled "performance" for the 1st-is-performance branch, sibling
  // performances must remain selectable for the 2nd preference. Same-value
  // uniqueness (you cannot pick the same exact performance twice) is
  // enforced at the option-list level below by skipping `firstPreference`.
  const allowedCategories = useMemo<PreferenceOption[]>(() => {
    if (!firstCategory) return ['slack', 'out', 'performance'];
    return firstCategory === 'slack' ? config.ifSlack : config.ifPerformance;
  }, [config, firstCategory]);

  // Build the 2nd-preference dropdown by walking the option list once and
  // keeping rows whose category is allowed and whose value differs from
  // the 1st pick. "Draw Out" is appended as a synthetic option when its
  // category is allowed.
  const secondOptions = useMemo<PerformanceChoice[]>(() => {
    const out: PerformanceChoice[] = [];
    for (const opt of allOptions) {
      const cat: PreferenceOption = opt.isSlack ? 'slack' : 'performance';
      if (!allowedCategories.includes(cat)) continue;
      if (opt.value === firstPreference) continue;
      out.push(opt);
    }
    if (allowedCategories.includes('out')) {
      out.push({ value: DRAW_OUT_VALUE, label: DRAW_OUT_VALUE, isSlack: false });
    }
    return out;
  }, [allowedCategories, allOptions, firstPreference]);

  // Forced-out edge case: zero allowed 2nd-pref categories. Force "Draw Out"
  // onto the form and render a read-only label so the user can still submit.
  const forcedOut = firstCategory != null && allowedCategories.length === 0;
  const forcedOutOnly =
    firstCategory != null &&
    allowedCategories.length === 1 &&
    allowedCategories[0] === 'out';

  useEffect(() => {
    if (forcedOut && secondPreference !== DRAW_OUT_VALUE) {
      onSecondChange(DRAW_OUT_VALUE);
    }
  }, [forcedOut, secondPreference, onSecondChange]);

  // If 1st prefs change and the existing 2nd pref is no longer valid, clear it.
  useEffect(() => {
    if (!secondPreference) return;
    const stillValid = secondOptions.some((o) => o.value === secondPreference);
    if (!stillValid && !forcedOut) {
      onSecondChange('');
    }
  }, [secondOptions, secondPreference, forcedOut, onSecondChange]);

  // Cross-entry warning: surfaced when the user picks something for THIS
  // event that doesn't line up with their last entry in another event in
  // the same rodeo. We pre-fill from the previous entry up front so the
  // warning only appears after a deliberate change. Buddy-group locked
  // prefs short-circuit this — those values are already authoritative
  // across the whole rodeo and can't be edited per-event.
  const previousMismatch = useMemo(() => {
    if (lockedByBuddyGroup) return null;
    if (!previousPreferences) return null;
    const normalize = (val: string | null | undefined): string => {
      if (!val) return '';
      return val.trim().replace(/\s*\[[^\]]*\]\s*$/, '').toLowerCase();
    };
    const firstDiffers =
      firstPreference !== '' &&
      normalize(firstPreference) !== normalize(previousPreferences.first);
    const secondDiffers =
      secondPreference !== '' &&
      normalize(secondPreference) !== normalize(previousPreferences.second);
    if (!firstDiffers && !secondDiffers) return null;
    return { firstDiffers, secondDiffers };
  }, [firstPreference, secondPreference, previousPreferences, lockedByBuddyGroup]);

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'}>
      {lockedByBuddyGroup && (
        <div className="flex items-start gap-2 rounded-md border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs">
          <Lock className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <span className="text-blue-700 dark:text-blue-300">
            {compact ? (
              <>Preferences set by your buddy group primary — change them from the rodeo&apos;s Buddy Group setup page.</>
            ) : (
              <>Set by buddy group primary — this preference applies to every event you enter in this rodeo. The primary cardholder can change it from the rodeo&apos;s Buddy Group setup page.</>
            )}
          </span>
        </div>
      )}
      {previousMismatch && previousPreferences && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <span className="text-amber-700 dark:text-amber-300">
            Heads up — these picks don&apos;t match your last entry in this rodeo
            (1st: <strong>{formatPreferenceLabel(previousPreferences.first, timezone) || '—'}</strong>
            {previousPreferences.second
              ? <>, 2nd: <strong>{formatPreferenceLabel(previousPreferences.second, timezone) || '—'}</strong></>
              : null}
            ). Different events in the same rodeo can have different
            preferences, but if that wasn&apos;t intentional you may want to
            line them up.
          </span>
        </div>
      )}
      <div className={compact ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : 'space-y-4'}>
        <div className="space-y-2">
          <Label>First Preference</Label>
          <Select
            value={firstPreference}
            onValueChange={onFirstChange}
            disabled={lockedByBuddyGroup}
          >
            <SelectTrigger disabled={lockedByBuddyGroup}>
              <SelectValue placeholder="Select first preference..." />
            </SelectTrigger>
            <SelectContent>
              {firstOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!compact && (
            <p className="text-xs text-muted-foreground">
              &quot;Draw Out&quot; cannot be selected as the first preference.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Second Preference</Label>
          {forcedOut || forcedOutOnly ? (
            <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
              <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="text-amber-700 dark:text-amber-300">
                Must choose <strong>Out</strong> — no other 2nd-preference options
                are allowed for this rodeo.
              </span>
            </div>
          ) : (
            <Select
              value={secondPreference}
              onValueChange={onSecondChange}
              disabled={lockedByBuddyGroup}
            >
              <SelectTrigger
                disabled={
                  lockedByBuddyGroup ||
                  !firstPreference ||
                  secondOptions.length === 0
                }
              >
                <SelectValue
                  placeholder={
                    !firstPreference
                      ? 'Pick a 1st preference first...'
                      : secondOptions.length === 0
                        ? 'No options available'
                        : 'Select second preference...'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {secondOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!compact && !forcedOut && !forcedOutOnly && (
            <p className="text-xs text-muted-foreground">
              Available options are filtered by the rodeo&apos;s rules and exclude
              your 1st choice.
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-500 text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
